import { logEvent } from "@src/appV2/lib/analytics";
import { buildWorkerStatisticsUrl } from "@src/appV2/redesign/InHome/lib/useHomeHealthStatistics";
import { type BookVisitRequest, type VisitWithCaseData } from "@src/appV2/redesign/InHome/types";
import { getVisitQueryKey } from "@src/appV2/redesign/InHome/useGetVisitData";
import { getVisitsQueryKey } from "@src/appV2/redesign/InHome/useGetVisitsWithCaseData";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { useBookVisit } from "../BookingBottomSheet/useBookVisit";
import { IN_HOME_USER_EVENTS } from "../lib/events";

export function useDropVisit(
  visitId: string,
  options: UseMutationOptions<VisitWithCaseData, string, BookVisitRequest> = {}
) {
  const queryClient = useQueryClient();
  const { userId: workerId } = useDefinedWorker();

  const bookVisitMutation = useBookVisit(visitId, {
    onSuccess: (visit) => {
      logEvent(IN_HOME_USER_EVENTS.VISIT_DROPPED, {
        caseId: visit.attributes.caseId,
        visitId: visit.id,
        workerReq: visit.attributes.workerReq,
        type: visit.attributes.type,
      });
      void queryClient.invalidateQueries(getVisitsQueryKey);
      void queryClient.invalidateQueries(getVisitQueryKey(visitId, visit.attributes.caseId ?? ""));
      void queryClient.invalidateQueries([buildWorkerStatisticsUrl(workerId)]);
    },
    ...options,
  });

  return {
    ...bookVisitMutation,
    mutateAsync: async () =>
      await bookVisitMutation.mutateAsync({
        data: { attributes: { bookedWorkerId: null }, type: "visit" },
      }),
    mutate: () => {
      bookVisitMutation.mutate({
        data: { attributes: { bookedWorkerId: null }, type: "visit" },
      });
    },
  };
}
